import { ComponentProps, FC, useMemo, useState } from 'react'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import { useCRMContext } from '../../CRMContext'
import amclasses from '@pages/Admin/CRM/styles/ammodal.module.css'
import classes from './index.module.css'
import { Modal } from 'antd'
import { wazzupIntegrationApi } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { selectProfile } from '@store/profile.reducer'
import { useSelector } from 'react-redux'
import cn from 'classnames'
import {
  CRM_MAX_LEAD_CHAT_INTEGRATION,
  CRM_TG_LEAD_CHAT_INTEGRATION,
  CRM_VK_LEAD_CHAT_INTEGRATION,
  CRM_WAZZUP_LEAD_CHAT_INTEGRATION,
  CRMIntegrationType,
  getCrmIntegrations,
} from './integrations'
import { getDisabledIntegrationsForCRMPhoneField } from '@pages/Admin/CRMSettings/tabs/ProfilePage/components/ProfileFieldItem/utils'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import {
  formatCrmChatUnreadBadge,
  getLeadProviderChatMeta,
  getLeadTelephonyEndpoints,
  getProviderUnreadCount,
  getVkLeadChatButtonState,
  getVkUnreadCount,
  leadHasProviderChat,
  VK_LEAD_CHAT_BUTTON_LABEL,
} from '@pages/Admin/CRM/utils/leadFields'
import { isVkIntegrationUnreadBadgesEnabled } from '@pages/Admin/CRM/utils/vkIntegrationLeadChat'
import { useCrmIntegrationChatUserAccess } from '@/hooks/useCrmIntegrationChatUserAccess'
import { useCrmIntegrationChatAvailability } from '@/hooks/useCrmIntegrationChatAvailability'
import {
  buildIntegrationCrmAccessibilityMap,
} from '@pages/Admin/CRM/utils/crmIntegrationAccess'
import { getCrmIntegrationAccessDeniedTooltip } from '@pages/Admin/CRM/components/integrations/common/lib/crmIntegrationAccessDeniedTooltip'
import { PhoneIcon } from '@/utils/icons'
import CrmProviderChatButton from '../CrmProviderChatButton/CrmProviderChatButton'
import {
  buildLeadProviderChatMenuAccounts,
  hasAccessibleLeadProviderChatMenuDialog,
} from '@pages/Admin/CRM/utils/leadProviderChatMenu'

interface PhoneIntegrationButtonsProps {
  tooltipProps?: ComponentProps<typeof TooltipWrapper>
  leadId: number
  fieldId: number
  fieldSettings: unknown
  /** Лид / строка таблицы / канбан с `integrations` или legacy `vk`. */
  vkLeadSource: unknown
  onOpenVkChat: (options?: { conversationId?: number }) => void
  onOpenMaxChat?: (options?: { conversationId?: number }) => void
  onOpenTelegramChat?: (options?: { conversationId?: number }) => void
  onOpenWazzupChat?: (options?: { conversationId?: number }) => void
  vkUnreadCount?: number
  maxUnreadCount?: number
  telegramUnreadCount?: number
  wazzupUnreadCount?: number
  /** `table` — таблица лидов; `card` — карточка лида; `kanban` — канбан-карточка. */
  vkUnreadBadgeSize?: 'table' | 'card' | 'kanban'
  /** Только кнопки CRM-чатов (без iframe Wazzup). */
  vkOnly?: boolean
  /** Чат ВК — только у телефона лида (PHONE), не родителя. */
  showVkChatButton?: boolean
  /** Чат MAX — показывать, если у лида есть integrations.max. */
  showMaxChatButton?: boolean
  /** Чат Telegram — показывать, если у лида есть integrations.telegram. */
  showTelegramChatButton?: boolean
  /** Чат Wazzup CRM — показывать, если у лида есть integrations.wazzup. */
  showWazzupChatButton?: boolean
  /** Legacy iframe Wazzup — только когда телефон совпадает с сохранённым. */
  showWazzupIntegrations?: boolean
}

const PhoneIntegrationButtons: FC<PhoneIntegrationButtonsProps> = ({
  tooltipProps,
  leadId,
  fieldId,
  fieldSettings,
  vkLeadSource,
  onOpenVkChat,
  onOpenMaxChat,
  onOpenTelegramChat,
  onOpenWazzupChat,
  vkUnreadCount = 0,
  maxUnreadCount = 0,
  telegramUnreadCount = 0,
  wazzupUnreadCount = 0,
  vkUnreadBadgeSize = 'table',
  vkOnly = false,
  showVkChatButton = true,
  showMaxChatButton = true,
  showTelegramChatButton = true,
  showWazzupChatButton = true,
  showWazzupIntegrations = true,
}) => {
  const addAlert = useAddAlert()
  const profile = useSelector(selectProfile)
  const { wazzupIntegrationsData, vkIntegration } = useCRMContext()
  const hasIntegrationChatAccess = useCrmIntegrationChatUserAccess()
  const { connectedIntegrations, isLoading: integrationsLoading } =
    useCrmIntegrationChatAvailability()

  const accessByIntegrationId = useMemo(() => {
    if (integrationsLoading || connectedIntegrations.length === 0) {
      return undefined
    }
    return buildIntegrationCrmAccessibilityMap(connectedIntegrations)
  }, [connectedIntegrations, integrationsLoading])

  const buildMenu = (provider: CrmChatProviderKey) =>
    buildLeadProviderChatMenuAccounts(
      getLeadProviderChatMeta(vkLeadSource, provider),
      provider,
      { accessByIntegrationId },
    )

  const sumAccessibleMenuUnread = (
    accounts: ReturnType<typeof buildLeadProviderChatMenuAccounts>,
  ): number =>
    accounts.reduce((sum, account) => {
      if (account.isAccessible === false) return sum

      if (account.channels) {
        const fromChannels = account.channels.reduce((channelSum, channel) => {
          if (channel.isAccessible === false) return channelSum
          const channelCount = Number(channel.unread_count ?? 0)
          if (Number.isFinite(channelCount) && channelCount > 0) {
            return channelSum + Math.floor(channelCount)
          }
          return (
            channelSum +
            channel.dialogs.reduce((dialogSum, dialog) => {
              if (dialog.isAccessible === false) return dialogSum
              const count = Number(dialog.unread_count ?? 0)
              return (
                dialogSum +
                (Number.isFinite(count) && count > 0 ? Math.floor(count) : 0)
              )
            }, 0)
          )
        }, 0)
        if (fromChannels > 0) {
          return sum + fromChannels
        }
        const accountCount = Number(account.unread_count ?? 0)
        return (
          sum +
          (Number.isFinite(accountCount) && accountCount > 0
            ? Math.floor(accountCount)
            : 0)
        )
      }

      const count = Number(account.unread_count ?? 0)
      return (
        sum + (Number.isFinite(count) && count > 0 ? Math.floor(count) : 0)
      )
    }, 0)

  const vkChatButton = getVkLeadChatButtonState(vkIntegration, vkLeadSource)
  const showVkUnreadBadge = isVkIntegrationUnreadBadgesEnabled(vkIntegration)

  const vkMenu = buildMenu('vkontakte')
  const maxMenu = buildMenu('max')
  const telegramMenu = buildMenu('telegram')
  const wazzupMenu = buildMenu('wazzup')

  const hasAccessibleVkTabs = hasAccessibleLeadProviderChatMenuDialog(vkMenu)
  const hasAccessibleMaxTabs = hasAccessibleLeadProviderChatMenuDialog(maxMenu)
  const hasAccessibleTelegramTabs =
    hasAccessibleLeadProviderChatMenuDialog(telegramMenu)
  const hasAccessibleWazzupTabs =
    hasAccessibleLeadProviderChatMenuDialog(wazzupMenu)

  const hasInaccessibleVkTabs =
    !integrationsLoading &&
    connectedIntegrations.length > 0 &&
    vkMenu.length > 0 &&
    !hasAccessibleVkTabs
  const hasInaccessibleMaxTabs =
    !integrationsLoading &&
    connectedIntegrations.length > 0 &&
    maxMenu.length > 0 &&
    !hasAccessibleMaxTabs
  const hasInaccessibleTelegramTabs =
    !integrationsLoading &&
    connectedIntegrations.length > 0 &&
    telegramMenu.length > 0 &&
    !hasAccessibleTelegramTabs
  const hasInaccessibleWazzupTabs =
    !integrationsLoading &&
    connectedIntegrations.length > 0 &&
    wazzupMenu.length > 0 &&
    !hasAccessibleWazzupTabs

  const integrationAccessDeniedTooltip =
    'Нет доступа к чатам этого лида. Обратитесь к администратору платформы.'

  const canShowVkUnreadBadge =
    showVkUnreadBadge && vkChatButton.isEnabled && !hasInaccessibleVkTabs

  const displayVkUnreadCount = canShowVkUnreadBadge
    ? Math.max(
        vkUnreadCount,
        sumAccessibleMenuUnread(vkMenu) || getVkUnreadCount(vkLeadSource),
      )
    : 0

  const showMaxButton =
    showMaxChatButton && leadHasProviderChat(vkLeadSource, 'max')
  const showTelegramButton =
    showTelegramChatButton && leadHasProviderChat(vkLeadSource, 'telegram')
  const showWazzupButton =
    showWazzupChatButton && leadHasProviderChat(vkLeadSource, 'wazzup')

  const displayMaxUnreadCount = Math.max(
    maxUnreadCount,
    sumAccessibleMenuUnread(maxMenu) ||
      getProviderUnreadCount(vkLeadSource, 'max'),
  )

  const displayTelegramUnreadCount = Math.max(
    telegramUnreadCount,
    sumAccessibleMenuUnread(telegramMenu) ||
      getProviderUnreadCount(vkLeadSource, 'telegram'),
  )

  const displayWazzupUnreadCount = Math.max(
    wazzupUnreadCount,
    sumAccessibleMenuUnread(wazzupMenu) ||
      getProviderUnreadCount(vkLeadSource, 'wazzup'),
  )

  const telephonyEndpoints = getLeadTelephonyEndpoints(vkLeadSource)

  const [stateModal, setStateModal] = useState<{
    open: boolean
    type?: CRMIntegrationType
    iframeUrl?: string
  }>({ open: false })

  const integrations = getCrmIntegrations(wazzupIntegrationsData)

  const disabledIntegrations = useMemo(
    () => getDisabledIntegrationsForCRMPhoneField(fieldSettings),
    [fieldSettings],
  )

  const vkButton =
    showVkChatButton && hasIntegrationChatAccess ? (
      <CrmProviderChatButton
        icon={CRM_VK_LEAD_CHAT_INTEGRATION.icon}
        iconColor={CRM_VK_LEAD_CHAT_INTEGRATION.color}
        label={VK_LEAD_CHAT_BUTTON_LABEL}
        enabled={vkChatButton.isEnabled && !hasInaccessibleVkTabs}
        disabledTooltip={
          hasInaccessibleVkTabs
            ? integrationAccessDeniedTooltip
            : vkChatButton.tooltipTitle
        }
        enabledTooltip={vkChatButton.tooltipTitle}
        unreadLabel={formatCrmChatUnreadBadge(displayVkUnreadCount)}
        badgeSize={vkUnreadBadgeSize}
        menuAccounts={vkMenu}
        accessDeniedTooltip={getCrmIntegrationAccessDeniedTooltip('vkontakte')}
        tooltipProps={tooltipProps}
        onOpenChat={onOpenVkChat}
      />
    ) : null

  const maxButton =
    showMaxButton && hasIntegrationChatAccess ? (
      <CrmProviderChatButton
        icon={CRM_MAX_LEAD_CHAT_INTEGRATION.icon}
        iconColor={CRM_MAX_LEAD_CHAT_INTEGRATION.color}
        label={CRM_MAX_LEAD_CHAT_INTEGRATION.label}
        enabled={!hasInaccessibleMaxTabs}
        disabledTooltip={
          hasInaccessibleMaxTabs ? integrationAccessDeniedTooltip : null
        }
        enabledTooltip={CRM_MAX_LEAD_CHAT_INTEGRATION.label}
        unreadLabel={formatCrmChatUnreadBadge(displayMaxUnreadCount)}
        badgeSize={vkUnreadBadgeSize}
        menuAccounts={maxMenu}
        accessDeniedTooltip={getCrmIntegrationAccessDeniedTooltip('max')}
        tooltipProps={tooltipProps}
        onOpenChat={onOpenMaxChat}
      />
    ) : null

  const telegramButton =
    showTelegramButton && hasIntegrationChatAccess ? (
      <CrmProviderChatButton
        icon={CRM_TG_LEAD_CHAT_INTEGRATION.icon}
        iconColor={CRM_TG_LEAD_CHAT_INTEGRATION.color}
        label={CRM_TG_LEAD_CHAT_INTEGRATION.label}
        enabled={!hasInaccessibleTelegramTabs}
        disabledTooltip={
          hasInaccessibleTelegramTabs ? integrationAccessDeniedTooltip : null
        }
        enabledTooltip={CRM_TG_LEAD_CHAT_INTEGRATION.label}
        unreadLabel={formatCrmChatUnreadBadge(displayTelegramUnreadCount)}
        badgeSize={vkUnreadBadgeSize}
        menuAccounts={telegramMenu}
        accessDeniedTooltip={getCrmIntegrationAccessDeniedTooltip('telegram')}
        tooltipProps={tooltipProps}
        onOpenChat={onOpenTelegramChat}
      />
    ) : null

  const wazzupButton =
    showWazzupButton && hasIntegrationChatAccess ? (
      <CrmProviderChatButton
        icon={CRM_WAZZUP_LEAD_CHAT_INTEGRATION.icon}
        iconColor={CRM_WAZZUP_LEAD_CHAT_INTEGRATION.color}
        label={CRM_WAZZUP_LEAD_CHAT_INTEGRATION.label}
        enabled={!hasInaccessibleWazzupTabs}
        disabledTooltip={
          hasInaccessibleWazzupTabs ? integrationAccessDeniedTooltip : null
        }
        enabledTooltip={CRM_WAZZUP_LEAD_CHAT_INTEGRATION.label}
        unreadLabel={formatCrmChatUnreadBadge(displayWazzupUnreadCount)}
        badgeSize={vkUnreadBadgeSize}
        menuAccounts={wazzupMenu}
        accessDeniedTooltip={getCrmIntegrationAccessDeniedTooltip('wazzup')}
        tooltipProps={tooltipProps}
        onOpenChat={onOpenWazzupChat}
      />
    ) : null

  if (vkOnly) {
    return (
      <div className={classes.list}>
        {vkButton}
        {maxButton}
        {telegramButton}
        {wazzupButton}
      </div>
    )
  }

  return (
    <div className={classes.list}>
      {showWazzupIntegrations
        ? telephonyEndpoints.map((endpoint: any) => (
            <TooltipWrapper
              key={`telephony-${endpoint.id}`}
              title={endpoint.name}
              {...tooltipProps}
            >
              <div
                className={cn(
                  classes.linkWrapper,
                  classes.telephonyLinkWrapper,
                )}
              >
                <PhoneIcon style={{ fill: 'var(--color-button-primary-bg)' }} />
                {formatCrmChatUnreadBadge(Number(endpoint.unread_count ?? 0)) ? (
                  <span
                    className={cn(
                      classes.vkUnreadBadge,
                      classes[`vkUnreadBadge_${vkUnreadBadgeSize}`],
                    )}
                    aria-label="Пропущенные звонки"
                  >
                    {formatCrmChatUnreadBadge(Number(endpoint.unread_count ?? 0))}
                  </span>
                ) : null}
              </div>
            </TooltipWrapper>
          ))
        : null}
      {showWazzupIntegrations
        ? integrations
            .filter((i) => !i.is_disabled_for_lead)
            .filter((i) =>
              !i.is_active ? true : !disabledIntegrations.includes(i.key),
            )
            .map(({ key, icon: Icon, is_active, color }) => (
              <TooltipWrapper
                key={key}
                title={!is_active ? 'Интеграция ещё не подключена' : null}
                {...tooltipProps}
              >
                <div
                  className={classes.linkWrapper}
                  style={{ cursor: is_active ? 'pointer' : 'default' }}
                  onClick={() => {
                    if (!is_active) return

                    // FIXME: потом исправить
                    if (key === 'phone') return

                    wazzupIntegrationApi
                      .generateChatIframe({
                        account_id: wazzupIntegrationsData?.account?.id,
                        chatType: key,
                        phone_field_id: fieldId,
                        lead_id: leadId,
                        user_id: profile.id,
                      })
                      .then((res) => {
                        if (res?.data?.data?.iframe_url) {
                          setStateModal({
                            open: true,
                            type: key,
                            iframeUrl: res.data.data.iframe_url,
                          })
                        }
                      })
                      .catch(() =>
                        addAlert({
                          title: 'Ошибка',
                          text: 'Не удалось загрузить интеграцию',
                          type: 'error',
                        }),
                      )
                  }}
                >
                  <Icon
                    style={{
                      fill: is_active
                        ? color
                        : 'var(--color-button-icon-disabled-soft)',
                    }}
                  />
                </div>
              </TooltipWrapper>
            ))
        : null}

      {vkButton}
      {maxButton}
      {telegramButton}
      {wazzupButton}

      <Modal
        open={stateModal.open}
        onOk={() => setStateModal({ open: false })}
        onCancel={() => setStateModal({ open: false })}
        footer={null}
        title={null}
        centered
        maskClosable
        className={cn(amclasses.modal, classes.iframeModal)}
      >
        <iframe src={stateModal.iframeUrl} className={classes.iframe} />
      </Modal>
    </div>
  )
}

export default PhoneIntegrationButtons
